/**
 * Tooltip formatter compartilhado dos graficos Windx (SH + SM).
 *
 * Bug PO 2026-05-27: com muitas usinas selecionadas (15 usinas × 11 modelos
 * = ate 165 series), o tooltip default do ECharts mostra TODAS as series no
 * eixo X hovered, incluindo as que tem valor null/missing (renderizadas
 * como `—`). Sem max-height + scroll, o tooltip ultrapassa o viewport e o
 * usuario nao consegue ler todos os valores.
 *
 * Solucao A+B (combinacao):
 * - A: FILTRAR series com valor nulo/missing (reduz 165 -> ~12 numa rodada
 *   tipica). Less is more (Nielsen Norman: signal-to-noise).
 * - B: SCROLL defensivo via `max-height: 60vh; overflow-y: auto` no
 *   container do tooltip (set via `extraCssText` no objeto tooltip).
 *
 * Aplicado em `ChartComSliders` que e o wrapper comum a GraficoSH/GraficoSM.
 * Comparativo (TabelaComparativoSM) NAO usa este wrapper (eh tabela) entao
 * nao eh impactado.
 */

/**
 * Shape de um item de tooltip do ECharts no `trigger: 'axis'`.
 *
 * O ECharts passa um array de objetos com este formato para a funcao
 * `formatter` quando o trigger eh por eixo. Tipagem minima — so o que
 * usamos.
 */
export interface TooltipAxisParam {
  axisValueLabel?: string;
  axisValue?: string | number;
  marker?: string;
  seriesName?: string;
  value?: unknown;
  data?: unknown;
  // Cor da serie (ECharts injeta) — usada para colorir o marcador de linha.
  color?: string;
  // Indice da serie no array `option.series` — casa com o lookup de lineStyle.
  seriesIndex?: number;
}

/**
 * Formata um numero para o tooltip do grafico.
 *
 * Mantem 2 casas decimais e usa locale pt-BR (virgula como separador).
 * Pode receber number, string (legado), ou null/undefined (caller filtra
 * antes de chamar — por seguranca, retorna string vazia se nao numerico).
 */
function formatNumero(valor: unknown): string {
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Indica se um valor de serie ECharts e "valido" (deve aparecer no tooltip).
 *
 * Considera invalidos: null, undefined, NaN, string vazia, e o marcador
 * comum `—` que o ECharts as vezes injeta. Importante: 0 EH valido (caso
 * legitimo de geracao nula em um patamar). NaN nao eh valido.
 */
function isValorValido(valor: unknown): boolean {
  if (valor == null) return false;
  if (typeof valor === 'string' && valor.trim() === '') return false;
  if (typeof valor === 'string' && (valor === '—' || valor === '-')) return false;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n);
}

/**
 * Formatter HTML do tooltip do ECharts (trigger=axis).
 *
 * Recebe o array de `params` do ECharts e retorna HTML string. Filtra
 * series sem valor valido, formata cada valor com 2 casas decimais, e
 * monta uma linha por serie no formato:
 *
 *   <data hovered>
 *   <marker> <serie>: <valor>
 *   ...
 *
 * Se NENHUMA serie tem valor valido no X hovered, retorna string vazia
 * (ECharts entende como "nao mostrar tooltip"), o que evita um popup
 * vazio confuso.
 */
/**
 * Traco de uma serie ECharts (`lineStyle.type`) que o marcador do tooltip
 * reproduz. O builder do BE usa os tres: no SH, por entidade (1a solid, 2a
 * dashed, 3a dotted); no SM, a REN e dotted (WEOL-SM #99).
 */
export type EstiloLinha = 'solid' | 'dashed' | 'dotted';

/**
 * Marcador de LINHA (em vez da bolinha default do ECharts) para o tooltip.
 * Um traco curto colorido no estilo real da serie — solido, tracejado ou
 * pontilhado —, assim o usuario distingue, ex., "com RMV" (solida) de "sem RMV"
 * (tracejada), e a 3a entidade ou a REN (pontilhadas) da 2a entidade.
 */
export function markerLinha(cor: string, estilo: EstiloLinha): string {
  return '<span style="display:inline-block;width:18px;height:0;'
    + `border-top:3px ${estilo} ${cor};vertical-align:middle;margin-right:6px"></span>`;
}

/**
 * Traco da serie ECharts: `lineStyle.type` 'dashed' ou 'dotted'; qualquer outro
 * valor (ausente, 'solid', dash customizado) vale como solido.
 */
function estiloDaSerie(serie: unknown): EstiloLinha {
  const s = serie as { lineStyle?: { type?: unknown } } | null;
  const tipo = s?.lineStyle?.type;
  return tipo === 'dashed' || tipo === 'dotted' ? tipo : 'solid';
}

/** Lookup `seriesIndex -> traco` a partir do array `option.series`. */
function construirEstiloLookup(series?: unknown): Map<number, EstiloLinha> {
  const arr = Array.isArray(series) ? series : [];
  const mapa = new Map<number, EstiloLinha>();
  arr.forEach((s, i) => mapa.set(i, estiloDaSerie(s)));
  return mapa;
}

/**
 * Marcador de cada linha do tooltip: traco solido, tracejado ou pontilhado
 * refletindo o `lineStyle` real da serie. Fallback para o marker default do
 * ECharts (bolinha) se faltar cor ou o lookup de estilo.
 */
function construirMarker(p: TooltipAxisParam, estiloPorIndice?: Map<number, EstiloLinha>): string {
  if (p.color && estiloPorIndice && p.seriesIndex !== undefined) {
    return markerLinha(p.color, estiloPorIndice.get(p.seriesIndex) ?? 'solid');
  }
  return p.marker ?? '';
}

export function tooltipFormatterAxis(
  params: TooltipAxisParam[],
  estiloPorIndice?: Map<number, EstiloLinha>,
): string {
  if (!Array.isArray(params) || params.length === 0) return '';

  const validos = params.filter((p) => isValorValido(p.value));
  if (validos.length === 0) return '';

  const header = params[0].axisValueLabel
    ?? (params[0].axisValue !== undefined ? String(params[0].axisValue) : '');

  const linhas = validos.map((p) => {
    const marker = construirMarker(p, estiloPorIndice);
    const seriesName = p.seriesName ?? '';
    const valorFmt = formatNumero(p.value);
    return `${marker} ${seriesName}: <b>${valorFmt}</b>`;
  });

  return `<b>${header}</b><br/>${linhas.join('<br/>')}`;
}

/**
 * CSS extra aplicado ao container do tooltip ECharts. Habilita scroll
 * vertical quando o tooltip ultrapassa 60% da viewport.
 *
 * `pointer-events: auto` eh necessario para o mouse poder entrar no
 * tooltip e rolar — sem isto, o tooltip fecha quando o mouse sai do
 * eixo.
 */
export const TOOLTIP_EXTRA_CSS = 'max-height: 60vh; overflow-y: auto; pointer-events: auto;';

/**
 * Objeto tooltip ECharts completo para Windx (SH + SM).
 *
 * Faz merge defensivo com qualquer config que o BE tenha mandado
 * (preserva customizacoes futuras sem quebrar backward compat).
 *
 * - `trigger: 'axis'` — mostra todas as series no X hovered
 * - `confine: true` — mantem dentro da area do chart
 * - `appendToBody: true` — escapa overflow:hidden de wrappers superiores
 * - `enterable: true` — permite mouse entrar no tooltip (para scroll)
 * - `extraCssText` — max-height + scroll
 * - `formatter` — filtra nulos + format pt-BR
 */
export function buildTooltipConfig(
  tooltipOriginalDoBE?: Record<string, unknown>,
  series?: unknown,
): Record<string, unknown> {
  const estiloPorIndice = construirEstiloLookup(series);
  return {
    ...(tooltipOriginalDoBE ?? {}),
    trigger: 'axis',
    confine: true,
    appendToBody: true,
    enterable: true,
    extraCssText: TOOLTIP_EXTRA_CSS,
    formatter: (params: TooltipAxisParam[]) => tooltipFormatterAxis(params, estiloPorIndice),
  };
}
